import type { DataTableRow } from "@client-shared";
import type { IntensityPosition, IntensityZone } from "@shared/types/intensity";
import type { Product, ShipToInfo, Variant } from "@shared/types/product";
import type { WebStatus } from "@shared/services/pim/types";
import type { FilterFn } from "@tanstack/react-table";

/**
 * Lowercased search keys precomputed once per row so a keystroke never rebuilds
 * strings for the whole catalogue. Roaster names match as a substring; the
 * identifier fields match exactly or by prefix.
 */
export interface AdminCatalogueSearchIndex {
  readonly name: string;
  readonly ids: readonly string[];
}

/**
 * Presentation projection of one SKU, not a domain type: `Product`/`Variant`
 * stay the contract and this only flattens them for the table.
 */
export interface AdminCatalogueRow {
  /** Stable `productId:variantId` row key. */
  id: string;
  productId: string;
  itemNumber?: string;
  /** Undefined when the source has no parent, i.e. `Product.id` is the item number itself. */
  parentId?: string;
  title?: string;
  imageUrl?: string;
  bagSize?: string;
  vendorId?: string;
  vendorName?: string;
  intensityPosition?: IntensityPosition;
  intensityZone?: IntensityZone;
  character?: string;
  badges: string[];
  webStatus?: WebStatus;
  /** Not shown: carried so an edit can raise an approval request on the roaster's behalf. */
  vendorKey?: string;
  /** PIM `WEB_TERM` ("Short" or "Long"); not shown. */
  coffeeTerm?: string;
  /** Every channel's status for the region's business unit; an edit needs the storefront channel's own status. */
  shipToInfo?: ShipToInfo[];
  __searchIndex: AdminCatalogueSearchIndex;
}

export type AdminCatalogueTableRow = DataTableRow<AdminCatalogueRow>;

const normalise = (value: string | undefined): string => (value ?? "").trim().toLowerCase();

const buildSearchIndex = (row: Omit<AdminCatalogueRow, "__searchIndex">): AdminCatalogueSearchIndex => ({
  name: normalise(row.vendorName),
  ids: [row.vendorId, row.itemNumber, row.parentId].map(normalise).filter((value) => value !== ""),
});

const toRow = (product: Product, variant?: Variant): AdminCatalogueRow => {
  const itemNumber = variant?.id;
  const position = product.intensityPosition;
  const base: Omit<AdminCatalogueRow, "__searchIndex"> = {
    // A product with no variants still gets one row, keyed `productId:`.
    id: `${product.id}:${itemNumber ?? ""}`,
    productId: product.id,
    itemNumber,
    parentId: product.id === itemNumber ? undefined : product.id,
    // The sanitiser sets Variant.title to the bag size, so the parent title is the coffee's name.
    title: product.title ?? variant?.title,
    imageUrl: product.images?.[0]?.src,
    bagSize: variant?.bagSize,
    vendorId: product.vendorId,
    vendorName: product.vendorName,
    intensityPosition: position,
    // Edited independently of the position, so taken as the catalogue reports it.
    intensityZone: product.intensityZone,
    character: product.character,
    badges: product.badges ?? [],
    webStatus: variant?.webStatus,
    vendorKey: product.vendorKey,
    coffeeTerm: variant?.term,
    shipToInfo: variant?.shipToInfo,
  };
  return { ...base, __searchIndex: buildSearchIndex(base) };
};

/** One row per SKU. A product without variants still yields a single row and is never dropped. */
export const toCatalogueRows = (products: Product[]): AdminCatalogueRow[] =>
  products.flatMap((product) => {
    const variants = product.variants ?? [];
    return variants.length === 0 ? [toRow(product)] : variants.map((variant) => toRow(product, variant));
  });

/** Trimmed, case-insensitive match against a row's precomputed index. */
export const matchesCatalogueQuery = (index: AdminCatalogueSearchIndex, query: string): boolean => {
  const needle = normalise(query);
  if (needle === "") return true;
  return index.name.includes(needle) || index.ids.some((id) => id.startsWith(needle));
};

// `_addMeta` is unused but declared: `FilterFn` takes four parameters and every
// call site passes all four.
export const adminCatalogueGlobalFilterFn: FilterFn<AdminCatalogueTableRow> = (row, _columnId, filterValue, _addMeta) =>
  typeof filterValue !== "string" || matchesCatalogueQuery(row.original.__searchIndex, filterValue);

/** Catalogue fields tracked by the coverage metrics and the "Missing" filter. */
export const CATALOGUE_FIELDS = ["intensityZone", "intensityPosition", "character", "badges"] as const;

export type CatalogueField = (typeof CATALOGUE_FIELDS)[number];

/** Status dropdown preset for everything currently on the storefront. */
export const ACTIVE_STATUS_FILTER = "active";

const ACTIVE_WEB_STATUSES: ReadonlySet<WebStatus> = new Set(["Visible for Sale", "Visible but not for Sale"]);

const hasField = (
  source: Pick<AdminCatalogueRow, "intensityZone" | "intensityPosition" | "character" | "badges">,
  field: CatalogueField
): boolean => {
  switch (field) {
    case "intensityZone":
      return source.intensityZone !== undefined;
    case "intensityPosition":
      return source.intensityPosition !== undefined;
    case "character":
      return Boolean(source.character?.trim());
    case "badges":
      return source.badges.length > 0;
  }
};

/** Empty selection matches everything; the "active" preset expands to its statuses. */
export const matchesStatusSelection = (status: WebStatus | undefined, selected: readonly string[]): boolean => {
  if (selected.length === 0) return true;
  if (status === undefined) return false;
  return selected.includes(status) || (selected.includes(ACTIVE_STATUS_FILTER) && ACTIVE_WEB_STATUSES.has(status));
};

export const matchesBadgeSelection = (badges: readonly string[], selected: readonly string[]): boolean =>
  selected.length === 0 || badges.some((badge) => selected.includes(badge));

/** A row matches when it is missing any of the selected fields. */
export const matchesMissingSelection = (row: AdminCatalogueRow, missing: readonly CatalogueField[]): boolean =>
  missing.length === 0 || missing.some((field) => !hasField(row, field));

/** Distinct badge values across the catalogue, for the badge filter. */
export const collectBadgeOptions = (rows: readonly AdminCatalogueRow[]): string[] =>
  Array.from(new Set(rows.flatMap((row) => row.badges))).sort((a, b) => a.localeCompare(b));

export interface CatalogueFieldCoverage {
  field: CatalogueField;
  /** Parent products that have the field. */
  present: number;
  /** All parent products in the catalogue. */
  total: number;
}

/** Coverage per field, counted over parent products rather than SKUs. */
export const computeCatalogueCoverage = (products: readonly Product[]): CatalogueFieldCoverage[] => {
  const sources = products.map((product) => ({
    intensityPosition: product.intensityPosition,
    intensityZone: product.intensityZone,
    character: product.character,
    badges: product.badges ?? [],
  }));
  return CATALOGUE_FIELDS.map((field) => ({
    field,
    present: sources.filter((source) => hasField(source, field)).length,
    total: sources.length,
  }));
};

/** Reads a column filter value written by a DataTable filter group. */
export const toFilterSelection = (value: unknown): string[] =>
  Array.isArray(value) ? value.flatMap((item: unknown) => (typeof item === "string" ? [item] : [])) : [];
